<template>
  <div>
    <h2>{{ t('periodTable') }}</h2>
    <div class="mt-4 overflow-x-auto">
      <div
        :class="[
          table(),
          condensed
            ? 'grid-cols-[minmax(9rem,1.1fr)_max-content_minmax(14rem,1.5fr)_max-content]'
            : 'grid-cols-[minmax(9rem,1.1fr)_max-content_minmax(14rem,1.5fr)_max-content_auto_auto]',
        ]"
      >
        <div :class="tableHeader()">
          <div>{{ t('dates') }}</div>
          <div :class="tableCell({ numeric: true })">{{ t('totalBasePrice') }}</div>
          <div>{{ t('extras') }}</div>
          <div :class="tableCell({ numeric: true })">{{ t('periodPrice') }}</div>
          <div v-if="!condensed">{{ t('state') }}</div>
          <div v-if="!condensed" />
        </div>
        <div
          v-for="row in rows"
          :key="row.index"
          :class="[tableRow(), '!items-start', row.isCurrent && 'bg-obsidian-50']"
          data-testid="period-table-row"
        >
          <div>
            {{ formatDate(row.period.startDate, DateTime.DATE_SHORT) }} –
            {{ formatDate(row.period.endDate, DateTime.DATE_SHORT) }}
          </div>
          <div :class="tableCell({ numeric: true })"><span v-currency="row.basePrice" /></div>
          <div class="min-w-0">
            <ul v-if="row.extraLines.length" class="space-y-1">
              <li
                v-for="line in row.extraLines"
                :key="line.key"
                class="flex items-baseline justify-between gap-3 text-xs text-obsidian-500"
              >
                <span class="min-w-0 text-left">{{ line.label }}</span>
                <span class="shrink-0 tabular-nums">
                  <span v-currency="line.price" />
                </span>
              </li>
            </ul>
          </div>
          <div :class="tableCell({ numeric: true, class: 'font-medium' })">
            <span v-currency="row.periodPrice" />
          </div>
          <div v-if="!condensed">
            <Badge :variant="row.state === 'invoiced' ? 'success' : 'neutral'">
              {{ t(`states.${row.state}`) }}
            </Badge>
            <p v-if="row.invoiceNumbers.length" class="mt-1 text-xs text-obsidian-500">
              {{ row.invoiceNumbers.join(', ') }}
            </p>
          </div>
          <div v-if="!condensed" class="text-right">
            <CVButton
              v-if="row.canAdjust"
              variant="ghost"
              size="sm"
              class="h-auto px-2 py-0.5"
              @click.prevent="editingIndex = row.index"
            >
              {{ t('adjust') }}
            </CVButton>
          </div>
        </div>
        <div v-if="!condensed" :class="tableFooter({ muted: true })">
          <div class="col-span-3">{{ t('contractTotal') }}</div>
          <div :class="tableCell({ numeric: true, class: 'font-medium' })">
            <span v-currency="contractTotal" />
          </div>
          <div />
          <div />
        </div>
      </div>
    </div>

    <Modal v-if="!condensed" v-model="showForm" show-close>
      <template #title>{{ t('adjust') }}</template>
      <PeriodForm
        v-if="editingIndex != null && booking"
        :booking="booking"
        :period-index="editingIndex"
        @saved="editingIndex = null"
        @cancel="editingIndex = null"
      />
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import type { Booking } from '@/entities/bookings/booking.entity';
import type { Invoice } from '@/entities/invoices/invoice.entity';
import type { LongTermPricing } from '@carvia/ros-client-types';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { formatDate } from '@/hooks/use-formatted-date';
import {
  getContractTotal,
  getOverlappingPricingPeriodIndex,
  getPeriodBasePrice,
  getPeriodExtraLines,
  getPeriodPrice,
  getPricingPeriodState,
  invoiceNumbersForPeriod,
  getInvoicedPricingPeriodIndexes,
  isLongTermPricing,
  type PeriodExtraLine,
} from '@/utils/pricing.utils';
import { formatVehicleExtraValue } from '@/utils/format-vehicle-extra-value';
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import Badge from '@/components/Badge.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Modal from '@/components/Modal.vue';
import PeriodForm from './PeriodForm.vue';
import { table, tableCell, tableFooter, tableHeader, tableRow } from '@carvia/theme/lib/cva/grid';

const props = defineProps<{
  booking?: Booking;
  pricing?: LongTermPricing | null;
  invoices?: Invoice[];
  condensed?: boolean;
}>();

const { t } = useI18n();
const editingIndex = ref<number | null>(null);
const showForm = computed({
  get: () => editingIndex.value != null,
  set: (open) => {
    if (!open) {
      editingIndex.value = null;
    }
  },
});

const pricing = computed(() => {
  if (props.pricing) {
    return isLongTermPricing(props.pricing) ? props.pricing : null;
  }
  return props.booking && isLongTermPricing(props.booking.priceCalculation)
    ? props.booking.priceCalculation
    : null;
});
const contractTotal = computed(() => (pricing.value ? getContractTotal(pricing.value) : 0));
const overlappingIndex = computed(() =>
  props.condensed || !pricing.value ? null : getOverlappingPricingPeriodIndex(pricing.value),
);
const invoicedIndexes = computed(() => getInvoicedPricingPeriodIndexes(props.invoices ?? []));
const returned = computed(() =>
  props.booking
    ? [BookingStatus.CAR_RETURNED, BookingStatus.FINALIZED].includes(props.booking.status)
    : true,
);

function toExtraLineView(line: PeriodExtraLine): {
  key: string;
  label: string;
  price: number;
} {
  return {
    key: line.key,
    label: formatVehicleExtraValue(line.type, line.optionValue, line.fallbackName),
    price: line.price,
  };
}

const rows = computed(() => {
  if (!pricing.value) {
    return [];
  }
  return pricing.value.periods.map((period, index) => {
    const invoiced = invoicedIndexes.value.has(index);
    const state = getPricingPeriodState(period, invoiced);
    return {
      index,
      period,
      isCurrent: overlappingIndex.value === index,
      basePrice: getPeriodBasePrice(period),
      periodPrice: getPeriodPrice(period),
      state,
      invoiceNumbers: invoiceNumbersForPeriod(props.invoices ?? [], index),
      canAdjust: !props.condensed && !invoiced && !returned.value,
      extraLines: getPeriodExtraLines(period).map(toExtraLineView),
    };
  });
});
</script>

<i18n lang="json">
{
  "en": {
    "periodTable": "Pricing periods",
    "dates": "Dates",
    "totalBasePrice": "Car price",
    "extras": "Extras",
    "periodPrice": "Period price",
    "state": "State",
    "adjust": "Adjust",
    "contractTotal": "Contract total",
    "states": {
      "invoiced": "Invoiced",
      "started": "Started",
      "due": "Due",
      "unposted": "Unposted"
    }
  },
  "de": {
    "periodTable": "Preiszeiträume",
    "dates": "Zeitraum",
    "totalBasePrice": "Fahrzeugpreis",
    "extras": "Extras",
    "periodPrice": "Preis",
    "state": "Status",
    "adjust": "Anpassen",
    "contractTotal": "Gesamtpreis",
    "states": {
      "invoiced": "Abgerechnet",
      "started": "Gestartet",
      "due": "Fällig",
      "unposted": "Offen"
    }
  },
  "es": {
    "periodTable": "Periodos de precio",
    "dates": "Fechas",
    "totalBasePrice": "Precio del vehículo",
    "extras": "Extras",
    "periodPrice": "Precio del periodo",
    "state": "Estado",
    "adjust": "Ajustar",
    "contractTotal": "Total del contrato",
    "states": {
      "invoiced": "Facturado",
      "started": "Iniciado",
      "due": "Vencido",
      "unposted": "Pendiente"
    }
  }
}
</i18n>
